import apiClient, { getGuestId, API_BASE_URL } from "./config/axios";

// Re-export helpers
export { getGuestId, API_BASE_URL };

/**
 * Cart API module
 * Handles all shopping cart operations
 */
export const cartApi = {
    /**
     * Get cart
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getCart: async () => {
        try {
            const response = await apiClient.get("/api/cart");
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to fetch cart",
            };
        }
    },

    /**
     * Get cart count
     * @returns {Promise<{success: boolean, count?: number, message?: string}>}
     */
    getCartCount: async () => {
        try {
            const response = await apiClient.get("/api/cart/count");
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch cart count",
            };
        }
    },

    /**
     * Add product to cart
     * @param {object} productData - Product data to add
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    addProduct: async (productData) => {
        try {
            const response = await apiClient.post(
                "/api/cart/product",
                productData
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to add product to cart",
            };
        }
    },

    /**
     * Add deal to cart
     * @param {object} dealData - Deal data to add
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    addDeal: async (dealData) => {
        try {
            const response = await apiClient.post("/api/cart/deal", dealData);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to add deal to cart",
            };
        }
    },

    /**
     * Update cart item
     * @param {number} itemId - Cart item ID
     * @param {object} data - Update data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    updateItem: async (itemId, data) => {
        try {
            const response = await apiClient.put(
                `/api/cart/item/${itemId}`,
                data
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to update cart item",
            };
        }
    },

    /**
     * Remove cart item
     * @param {number} itemId - Cart item ID
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    removeItem: async (itemId) => {
        try {
            const response = await apiClient.delete(`/api/cart/item/${itemId}`);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to remove cart item",
            };
        }
    },

    /**
     * Clear cart
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    clearCart: async () => {
        try {
            const response = await apiClient.delete("/api/cart");
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to clear cart",
            };
        }
    },

    /**
     * Validate cart
     * @param {boolean} autoFix - Whether to auto-fix issues
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    validateCart: async (autoFix = false) => {
        try {
            const response = await apiClient.post("/api/cart/validate", {
                autoFix,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to validate cart",
            };
        }
    },

    /**
     * Sync cart on login
     * @param {array} localItems - Local cart items
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    syncCart: async (localItems) => {
        try {
            const response = await apiClient.post("/api/cart/sync", {
                items: localItems,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || "Failed to sync cart",
            };
        }
    },

    /**
     * Update customization group (update all items with same customizationGroupId)
     * @param {string} groupId - Customization group ID
     * @param {object} data - Update data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    updateCustomizationGroup: async (groupId, data) => {
        try {
            const response = await apiClient.put(
                `/api/cart/customization-group/${groupId}`,
                data
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to update customization group",
            };
        }
    },
};

/**
 * Order API module
 * Handles all order operations
 */
export const orderApi = {
    /**
     * Create order (checkout)
     * @param {object} orderData - Order data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    checkout: async (orderData) => {
        try {
            const response = await apiClient.post(
                "/api/orders/checkout",
                orderData
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to process checkout",
            };
        }
    },

    /**
     * Get user orders
     * @param {object} params - Query parameters
     * @returns {Promise<{success: boolean, data?: array, pagination?: object, message?: string}>}
     */
    getOrders: async (params = {}) => {
        try {
            const response = await apiClient.get("/api/orders", { params });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to fetch orders",
            };
        }
    },

    /**
     * Get order by ID
     * @param {number} orderId - Order ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getOrderById: async (orderId) => {
        try {
            const response = await apiClient.get(`/api/orders/${orderId}`);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to fetch order",
            };
        }
    },

    /**
     * Track order
     * @param {string} orderNumber - Order number
     * @param {string} email - Email address
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    trackOrder: async (orderNumber, email) => {
        try {
            const response = await apiClient.get(
                `/api/orders/track/${orderNumber}`,
                {
                    params: { email },
                }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to track order",
            };
        }
    },

    /**
     * Cancel order
     * @param {number} orderId - Order ID
     * @param {string} reason - Cancellation reason
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    cancelOrder: async (orderId, reason) => {
        try {
            const response = await apiClient.put(
                `/api/orders/${orderId}/cancel`,
                { reason }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to cancel order",
            };
        }
    },

    /**
     * Re-upload customization for rejected orders
     * @param {number} orderId - Order ID
     * @param {number} itemId - Item ID
     * @param {string} newImageUrl - New image URL
     * @param {object} customizationData - Customization data
     * @param {string} customizationGroupId - Customization group ID (optional)
     * @param {string} newText - New text for TEXT customizations (optional)
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    reuploadCustomization: async (
        orderId,
        itemId,
        newImageUrl,
        customizationData,
        customizationGroupId = null,
        newText = null
    ) => {
        try {
            const payload = {
                itemId,
                customizationGroupId,
            };

            // Include either newImageUrl or newText based on what's provided
            if (newImageUrl) {
                payload.newImageUrl = newImageUrl;
            }
            if (newText !== null) {
                payload.newText = newText;
            }
            if (customizationData) {
                payload.customizationData = customizationData;
            }

            const response = await apiClient.put(
                `/api/orders/${orderId}/reupload-customization`,
                payload
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to re-upload customization",
            };
        }
    },

    /**
     * Get item status history
     * @param {number} orderId - Order ID
     * @param {number} itemId - Item ID
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getItemStatusHistory: async (orderId, itemId) => {
        try {
            const response = await apiClient.get(
                `/api/orders/${orderId}/items/${itemId}/history`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch item history",
            };
        }
    },

    /**
     * Get logo upload status for an order (Add Logo Later feature)
     * @param {string} orderNumber - Order number
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getLogoUploadStatus: async (orderNumber) => {
        try {
            const response = await apiClient.get(
                `/api/orders/${orderNumber}/items/logo-status`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to get logo upload status",
            };
        }
    },

    /**
     * Upload logo for an order item (Add Logo Later feature)
     * @param {string} orderNumber - Order number
     * @param {number} itemId - Item ID
     * @param {FormData} formData - Form data with logo file
     * @param {function} onUploadProgress - Progress callback
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    uploadLogo: async (orderNumber, itemId, formData, onUploadProgress) => {
        try {
            const response = await apiClient.post(
                `/api/orders/${orderNumber}/items/${itemId}/upload-logo`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                    onUploadProgress,
                }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to upload logo",
            };
        }
    },
};

/**
 * Checkout API module
 * Handles checkout process
 */
export const checkoutApi = {
    /**
     * Initialize checkout - get cart data, user info, saved addresses
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    initCheckout: async (params = {}) => {
        try {
            const response = await apiClient.get("/api/checkout/init", {
                params,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to initialize checkout",
            };
        }
    },

    /**
     * Calculate totals with delivery option
     * @param {object} data - Calculation data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    calculateTotals: async (data) => {
        try {
            const response = await apiClient.post(
                "/api/checkout/calculate",
                data
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to calculate totals",
            };
        }
    },

    /**
     * Create Stripe payment intent
     * @param {object} checkoutData - Checkout data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    createPaymentIntent: async (checkoutData) => {
        try {
            const response = await apiClient.post(
                "/api/checkout/create-payment-intent",
                checkoutData
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to create payment intent",
            };
        }
    },

    /**
     * Complete checkout after payment
     * @param {object} orderData - Order data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    completeCheckout: async (orderData) => {
        try {
            const response = await apiClient.post(
                "/api/checkout/complete",
                orderData
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to complete checkout",
            };
        }
    },

    /**
     * Get countries and cities for address form
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getLocations: async () => {
        try {
            const response = await apiClient.get("/api/checkout/locations");
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch locations",
            };
        }
    },
};

/**
 * Delivery API module
 * Handles delivery types and estimation operations
 */
export const deliveryApi = {
    /**
     * Get all active delivery types
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getAllDeliveryTypes: async () => {
        try {
            const response = await apiClient.get(
                "/api/listbyparams/delivery-types",
                {
                    params: { isActive: true },
                }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch delivery types",
            };
        }
    },

    /**
     * Get delivery types for a specific product
     * @param {number} productId - Product ID
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getProductDeliveryTypes: async (productId) => {
        try {
            const response = await apiClient.get(
                `/api/products/${productId}/delivery-types`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch product delivery types",
            };
        }
    },

    /**
     * Get common delivery types for multiple products (for cart)
     * @param {array} productIds - Array of product IDs
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getCommonDeliveryTypes: async (productIds) => {
        try {
            const response = await apiClient.post(
                "/api/delivery-types/common",
                { productIds }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch common delivery types",
            };
        }
    },

    /**
     * Get delivery estimates for current cart
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getCartEstimates: async (overrides = {}) => {
        try {
            const params = {};
            if (overrides.globalDeliveryTypeId) {
                params.globalDeliveryTypeId = overrides.globalDeliveryTypeId;
            }
            if (
                overrides.productDeliveryTypeIds &&
                Object.keys(overrides.productDeliveryTypeIds).length > 0
            ) {
                params.productDeliveryTypeIds = JSON.stringify(
                    overrides.productDeliveryTypeIds
                );
            }

            const response = await apiClient.get("/api/delivery/estimate", {
                params,
            });
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch delivery estimates",
            };
        }
    },

    /**
     * Get delivery estimate for a specific product
     * @param {number} productId - Product ID
     * @param {boolean} hasCustomization - Whether item will be customized
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getProductEstimate: async (productId, hasCustomization = false) => {
        try {
            const response = await apiClient.get(
                `/api/delivery/estimate/product/${productId}`,
                {
                    params: { customized: hasCustomization },
                }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch product delivery estimate",
            };
        }
    },

    /**
     * Get current dispatch status (for countdown display)
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getDispatchStatus: async () => {
        try {
            const response = await apiClient.get(
                "/api/delivery/dispatch-status"
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch dispatch status",
            };
        }
    },
};

/**
 * Customization API module
 * Handles customization operations
 */
export const customizationApi = {
    /**
     * Get customization options for a product
     * @param {number} productId - Product ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getProductCustomizations: async (productId) => {
        try {
            const response = await apiClient.get(
                `/api/products/${productId}/customization-options`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch customization options",
            };
        }
    },

    /**
     * Get customization options for a deal
     * @param {number} dealId - Deal ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getDealCustomizations: async (dealId) => {
        try {
            const response = await apiClient.get(
                `/api/deals/${dealId}/customization-options`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch deal customization options",
            };
        }
    },

    /**
     * Get product-wise customization options for all products in a deal
     * @param {number} dealId - Deal ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getDealProductsCustomizations: async (dealId) => {
        try {
            const response = await apiClient.get(
                `/api/deals/${dealId}/products-customization-options`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch deal products customization options",
            };
        }
    },

    /**
     * Calculate customization price
     * @param {object} data - Calculation data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    calculatePrice: async (data) => {
        try {
            const response = await apiClient.post(
                "/api/customization/calculate-price",
                data
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to calculate price",
            };
        }
    },

    /**
     * Calculate deal customization price (product-wise)
     * @param {object} data - Calculation data
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    calculateDealPrice: async (data) => {
        try {
            const response = await apiClient.post(
                "/api/customization/calculate-deal-price",
                data
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to calculate deal customization price",
            };
        }
    },

    /**
     * Upload customization image
     * @param {File} file - Image file
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    uploadImage: async (file) => {
        try {
            const formData = new FormData();
            formData.append("image", file);
            const response = await apiClient.post(
                "/api/customization/upload",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to upload image",
            };
        }
    },
};

/**
 * Invoice API module
 * Handles invoice operations
 */
export const invoiceApi = {
    /**
     * Download invoice PDF for an order
     * @param {string} orderNumber - Order number
     * @param {string} email - Email address
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    downloadInvoice: async (orderNumber, email) => {
        try {
            const response = await apiClient.get(
                `/api/invoices/order/${orderNumber}`,
                {
                    params: { email },
                    responseType: "blob",
                }
            );

            // Create blob URL and trigger download
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Invoice-${orderNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to download invoice",
            };
        }
    },

    /**
     * View invoice in new tab
     * @param {string} orderNumber - Order number
     * @param {string} email - Email address
     * @returns {Promise<{success: boolean, message?: string}>}
     */
    viewInvoice: async (orderNumber, email) => {
        try {
            const response = await apiClient.get(
                `/api/invoices/order/${orderNumber}`,
                {
                    params: { email },
                    responseType: "blob",
                }
            );

            // Create blob URL and open in new tab
            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            window.open(url, "_blank");

            return { success: true };
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to view invoice",
            };
        }
    },

    /**
     * Get invoice URL (for embedding or direct access)
     * @param {string} orderNumber - Order number
     * @param {string} email - Email address
     * @returns {string} Invoice URL
     */
    getInvoiceUrl: (orderNumber, email) => {
        return `${API_BASE_URL}/api/invoices/order/${orderNumber}?email=${encodeURIComponent(
            email
        )}`;
    },
};

/**
 * Shared Basket API module
 * Handles sharing cart with others via unique link
 */
export const sharedBasketApi = {
    /**
     * Create a shared basket from current cart
     * @returns {Promise<{success: boolean, data?: {token: string, itemCount: number, totalAmount: number, expiresAt: string}, message?: string}>}
     */
    createSharedBasket: async () => {
        try {
            const response = await apiClient.post("/api/shared-basket");
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to create shared basket",
            };
        }
    },

    /**
     * Get a shared basket by token
     * @param {string} token - Unique basket token
     * @returns {Promise<{success: boolean, data?: object, message?: string, expired?: boolean}>}
     */
    getSharedBasket: async (token) => {
        try {
            const response = await apiClient.get(`/api/shared-basket/${token}`);
            return response.data;
        } catch (error) {
            // Check if it's an expired basket
            if (error.response?.status === 410) {
                return {
                    success: false,
                    message:
                        error.response?.data?.message ||
                        "This shared basket has expired",
                    expired: true,
                };
            }
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch shared basket",
            };
        }
    },

    /**
     * Add shared basket items to user's cart
     * @param {string} token - Unique basket token
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    addToCart: async (token) => {
        try {
            const response = await apiClient.post(
                `/api/shared-basket/${token}/add-to-cart`
            );
            return response.data;
        } catch (error) {
            // Check if it's an expired basket
            if (error.response?.status === 410) {
                return {
                    success: false,
                    message:
                        error.response?.data?.message ||
                        "This shared basket has expired",
                    expired: true,
                };
            }
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to add items to cart",
            };
        }
    },

    /**
     * Generate share URL for a token
     * @param {string} token - Unique basket token
     * @returns {string} Full shareable URL
     */
    getShareUrl: (token) => {
        // Use the frontend URL (window.location.origin)
        const baseUrl =
            typeof window !== "undefined" ? window.location.origin : "";
        return `${baseUrl}/shared-basket/${token}`;
    },
};

/**
 * Customization Proof API module
 * Handles proof approval workflow for customized items
 */
export const proofApi = {
    /**
     * Get proofs summary for customer (count of items awaiting approval)
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getProofsSummary: async () => {
        try {
            const response = await apiClient.get("/api/orders/proofs/summary");
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch proofs summary",
            };
        }
    },

    /**
     * Get proofs for a specific order item
     * @param {number} orderId - Order ID
     * @param {number} itemId - Order Item ID
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getItemProofs: async (orderId, itemId) => {
        try {
            const response = await apiClient.get(
                `/api/orders/${orderId}/items/${itemId}/proofs`
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to fetch item proofs",
            };
        }
    },

    /**
     * Approve a proof
     * @param {number} orderId - Order ID
     * @param {number} itemId - Item ID
     * @param {number} proofId - Proof ID
     * @param {string} feedback - Optional feedback message
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    approveProof: async (orderId, itemId, proofId, feedback = "") => {
        try {
            const response = await apiClient.post(
                `/api/orders/${orderId}/items/${itemId}/proofs/${proofId}/approve`,
                { feedback }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to approve proof",
            };
        }
    },

    /**
     * Request revision on a proof
     * @param {number} orderId - Order ID
     * @param {number} itemId - Item ID
     * @param {number} proofId - Proof ID
     * @param {string} feedback - Required feedback explaining what needs to change
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    requestRevision: async (orderId, itemId, proofId, feedback) => {
        try {
            const response = await apiClient.post(
                `/api/orders/${orderId}/items/${itemId}/proofs/${proofId}/revision`,
                { feedback }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message ||
                    "Failed to request revision",
            };
        }
    },

    /**
     * Add a comment to a proof
     * @param {number} orderId - Order ID
     * @param {number} itemId - Item ID
     * @param {number} proofId - Proof ID
     * @param {string} message - Comment message
     * @param {File} attachment - Optional attachment file
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    addComment: async (
        orderId,
        itemId,
        proofId,
        message,
        attachment = null
    ) => {
        try {
            const formData = new FormData();
            formData.append("message", message);
            if (attachment) {
                formData.append("attachment", attachment);
            }
            const response = await apiClient.post(
                `/api/orders/${orderId}/items/${itemId}/proofs/${proofId}/comments`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );
            return response.data;
        } catch (error) {
            return {
                success: false,
                message:
                    error.response?.data?.message || "Failed to add comment",
            };
        }
    },
};

// Default export with all APIs
export default {
    cartApi,
    orderApi,
    checkoutApi,
    customizationApi,
    deliveryApi,
    invoiceApi,
    sharedBasketApi,
    proofApi,
    getGuestId,
    API_BASE_URL,
};
